import { ApiResponse } from "./../types/profile-editing";
import {
  AllReportsResponse,
  GetFeedbackResponse,
  JobPositionResponse,
  MakeReportProps,
  MakeReportResponse,
  PartialProps,
  ReportResponse,
  ReportStatusResponse,
  ReportUiPreferencesRes,
  SendFeedbackProps,
  SendFeedbackResponse,
  UserMarketPrice,
} from "../types/api";
import axiosInstance from "../utils/axios";

export const getAllReports = async (lan: string = "en") => {
  try {
    const response = await axiosInstance.get<AllReportsResponse>(
      `/api/reports/?lang=${lan}`,
      {}
    );

    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const getReport = async (id: number, lang: string = "en") => {
  try {
    if (lang === "none") {
      const response = await axiosInstance.get<ApiResponse<ReportResponse>>(
        `/api/reports/${id}/`,
        {}
      );
      return response.data;
    } else {
      const response = await axiosInstance.get<ApiResponse<ReportResponse>>(
        `/api/reports/${id}/?lang=${lang}`,
        {}
      );
      return response.data;
    }
  } catch (error: any) {
    throw error;
  }
};

export const buildReport = async () => {
  try {
    const response = await axiosInstance.post<MakeReportResponse>(
      "/api/reports/"
    );
    return response.data;
  } catch (error: any) {
    if (error?.status === 404) {
      throw new Error("no report");
    } else if (error?.status === 400) {
      throw error;
    }
    throw new Error("Failed to fetch report");
  }
};

export const generateNewReport = async (credentials: MakeReportProps) => {
  try {
    if (credentials) {
      const response = await axiosInstance.put<MakeReportResponse>(
        `/api/reports/${credentials.id}/`,
        {
          ctx_overrides: {
            location: credentials.country,
            switch_to: credentials.profession,
          },
        }
      );
      return response.data;
    } else {
      const response = await axiosInstance.post<MakeReportResponse>(
        "/api/reports/"
      );
      return response.data;
    }
  } catch (error: any) {
    if (error?.status === 404) {
      throw new Error("no report");
    } else if (error?.status === 400) {
      throw error;
    }
    throw new Error("Failed to fetch report");
  }
};

export const partialBuildReport = async (props: PartialProps) => {
  try {
    const response = await axiosInstance.patch<MakeReportResponse>(
      `/api/reports/${props.reportId}/`,
      props.credentials
    );
    return response.data;
  } catch (error: any) {
    if (error?.status === 404) {
      throw new Error("no report");
    } else if (error?.status === 400) {
      throw error;
    }
    throw new Error("Failed to fetch report");
  }
};

export const getReportStatus = async (id: number) => {
  try {
    const response = await axiosInstance.get<ReportStatusResponse>(
      `/api/reports/${id}/status/`,
      {}
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getReportTranslateStatus = async (
  language: string,
  id: number
) => {
  try {
    const response = await axiosInstance.get<ReportStatusResponse>(
      `/api/reports/${id}/status/?lang=${language}`,
      {}
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendReportFeedback = async ({
  report_id,
  section,
  rating,
  thoughts,
}: SendFeedbackProps) => {
  try {
    const response = await axiosInstance.post<SendFeedbackResponse>(
      `/api/reports/${report_id}/feedback/`,
      {
        report: report_id,
        section: section,
        result_assessment: rating,
        thoughts: thoughts,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching report:", error);
    throw new Error("Failed to fetch report");
  }
};

export const getReportFeedback = async (report_id: number) => {
  try {
    const response = await axiosInstance.get<GetFeedbackResponse>(
      `/api/reports/${report_id}/feedback/`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching report:", error);
    throw new Error("Failed to fetch report");
  }
};

export const removeReport = async (report_id: number) => {
  try {
    const response = await axiosInstance.delete(`/api/reports/${report_id}/`);
    if (response.status === 204) return;
    return response.data;
  } catch (error) {
    console.error("Error fetching report:", error);
    throw new Error("Failed to fetch report");
  }
};

export const getJobDetails = async (slug: string, lang: string = "en") => {
  try {
    if (lang === "en") {
      const response = await axiosInstance.get<JobPositionResponse>(
        `/api/reports/job-passport/?slug=${slug}`
      );
      return response.data;
    } else {
      const response = await axiosInstance.get<JobPositionResponse>(
        `/api/reports/job-passport/?slug=${slug}&lang=${lang}`
      );
      return response.data;
    }
  } catch (error) {
    console.error("Error fetching report:", error);
    throw new Error("Failed to fetch report");
  }
};

export const getJobDetailsByTitle = async (
  slug: string,
  lang: string = "en"
) => {
  try {
    if (lang === "en") {
      const response = await axiosInstance.get<JobPositionResponse>(
        `/api/reports/job-passport/?title=${slug}`
      );
      return response.data;
    } else {
      const response = await axiosInstance.get<JobPositionResponse>(
        `/api/reports/job-passport/?title=${slug}&lang=${lang}`
      );
      return response.data;
    }
  } catch (error) {
    console.error("Error fetching report:", error);
    throw new Error("Failed to fetch report");
  }
};

export const changeUiPreferences = async (id: number, val: boolean) => {
  try {
    const response = await axiosInstance.patch<
      ApiResponse<ReportUiPreferencesRes>
    >(`/api/reports/${id}/update-ui-preferences/`, {
      show_context_override_hint: val,
    });
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const fetchUserPrice = async (lang: string = "en") => {
  try {
    const response = await axiosInstance.get<ApiResponse<UserMarketPrice>>(
      `/api/reports/user-market-price/?lang=${lang}`
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const processUserPrice = async () => {
  try {
    const response = await axiosInstance.post(
      `/api/reports/user-market-price/`,
      {}
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};
